import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  ApiEndpointConfig,
  ApiEndpointKey,
  ApiEndpoints,
  DropdownInfoConfig,
  DropdownOptionLike
} from '../../constants/api-endpoints';
import { ApiService, LanguageService } from '../../services';
import { TranslatePipe } from '../../pipes/translate.pipe';

interface DropdownViewOption {
  value: unknown;
  display: string;
  raw: DropdownOptionLike;
}

@Component({
  selector: 'app-common-dropdown',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  templateUrl: './common-dropdown.component.html',
  styleUrl: './common-dropdown.component.css'
})
export class CommonDropdownComponent implements OnChanges, OnDestroy {
  @Input() dropdownObj: Partial<DropdownInfoConfig> = {};
  @Input() requestFilters: Record<string, unknown> | null = null;
  @Input() compact = false;
  @Input() value: unknown = null;
  @Output() valueChange = new EventEmitter<{ name: string; value: unknown; option?: DropdownOptionLike }>();
  @Output() clear = new EventEmitter<{ name: string }>();
  @Output() optionsLoaded = new EventEmitter<{ name: string; options: DropdownOptionLike[] }>();

  resolvedConfig!: DropdownInfoConfig;
  options: DropdownViewOption[] = [];
  filteredOptions: DropdownViewOption[] = [];
  searchText = '';
  isLoading = false;
  error: string | null = null;
  isSearchMenuOpen = false;
  searchReadonly = false;
  private sourceOptions: DropdownOptionLike[] = [];
  private readonly languageSubscription: Subscription;

  constructor(
    private readonly api: ApiService,
    private readonly language: LanguageService
  ) {
    this.languageSubscription = this.language.language$.subscribe(() => {
      if (this.sourceOptions.length === 0) {
        return;
      }
      this.options = this.mapOptions(this.sourceOptions);
      this.filteredOptions = [...this.options];
      this.syncSearchTextFromValue();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const dropdownChanged = !!changes['dropdownObj'];
    const requestFiltersChanged = !!changes['requestFilters'];
    const valueChanged = !!changes['value'];

    if (dropdownChanged || requestFiltersChanged) {
      this.resolveConfig();
      this.loadOptions();
    }

    if (valueChanged) {
      this.applyInitialValue();
      this.syncSearchTextFromValue();
    }
  }

  ngOnDestroy(): void {
    this.languageSubscription.unsubscribe();
  }

  onSearchChange(): void {
    const query = this.searchText.trim().toLowerCase();
    if (!query) {
      this.filteredOptions = [...this.options];
      return;
    }

    this.filteredOptions = this.options.filter((item) => {
      const display1Value = `${this.readPathValue(item.raw, this.resolvedConfig.display1) ?? ''}`.toLowerCase();
      const display2Value = this.resolvedConfig.display2
        ? `${this.readPathValue(item.raw, this.resolvedConfig.display2) ?? ''}`.toLowerCase()
        : '';
      const fullDisplayValue = item.display.toLowerCase();

      return display1Value.includes(query)
        || display2Value.includes(query)
        || fullDisplayValue.includes(query);
    });
  }

  resolvedValue(): unknown {
    if (this.value === '' || this.value === undefined) {
      return null;
    }
    return this.value;
  }

  isHighlightedOption(option?: DropdownViewOption): boolean {
    return !!option?.raw['__highlighted'];
  }

  selectedOption(): DropdownViewOption | undefined {
    return this.options.find((option) =>
      option.value === this.value
      || `${option.value ?? ''}` === `${this.value ?? ''}`
    );
  }

  openSearchMenu(): void {
    this.isSearchMenuOpen = true;
    this.filteredOptions = [...this.options];
  }

  closeSearchMenu(): void {
    setTimeout(() => {
      this.isSearchMenuOpen = false;
      this.syncSearchTextFromValue();
    }, 120);
  }

  onSearchableInput(value: string): void {
    this.searchText = value;
    this.onSearchChange();
    this.isSearchMenuOpen = true;
  }

  selectOption(option: DropdownViewOption): void {
    this.onSelectionChange(option.value);
    this.isSearchMenuOpen = false;
  }

  onSelectionChange(selectedValue: unknown): void {
    this.value = selectedValue;
    const matched = this.options.find((option) =>
      option.value === selectedValue
      || `${option.value ?? ''}` === `${selectedValue ?? ''}`
    );
    this.searchText = matched?.display ?? '';
    this.valueChange.emit({
      name: this.resolvedConfig.name,
      value: selectedValue,
      option: matched?.raw
    });
  }

  clearSelection(event?: Event): void {
    event?.stopPropagation();
    event?.preventDefault();
    this.value = null;
    this.searchText = '';
    this.isSearchMenuOpen = false;
    this.valueChange.emit({
      name: this.resolvedConfig.name,
      value: null
    });
    this.clear.emit({ name: this.resolvedConfig.name });
  }

  private resolveConfig(): void {
    const safeDropdownObj = (this.dropdownObj ?? {}) as Partial<DropdownInfoConfig>;
    const preferredApiInfo = safeDropdownObj.apiInfo;
    const endpointConfig = preferredApiInfo ? this.resolveApiInfo(preferredApiInfo) : null;
    const endpointDropdownInfo = endpointConfig?.dropdownInfo ?? {};

    const merged = {
      ...endpointDropdownInfo,
      ...safeDropdownObj
    } as Partial<DropdownInfoConfig>;

    const label = merged.label ?? 'Option';
    this.resolvedConfig = {
      name: merged.name ?? '',
      valueKey: merged.valueKey ?? 'id',
      display1: merged.display1 ?? 'name',
      display2: merged.display2,
      label,
      placeholder: merged.placeholder ?? `Select ${label}`,
      options: merged.options ?? [],
      apiInfo: merged.apiInfo,
      status: merged.status,
      isSearchable: (merged as any).isSearchAble ?? merged.isSearchable ?? true,
      extraOptions: merged.extraOptions ?? [],
      hideLoadingMessage: merged.hideLoadingMessage ?? false,
      initialValue: merged.initialValue
    };
    this.searchReadonly = !!(safeDropdownObj as any)?.searchReadonly;
  }

  private loadOptions(): void {
    this.error = null;
    this.options = [];
    this.filteredOptions = [];
    this.sourceOptions = [];

    if (!this.resolvedConfig.name) {
      this.error = 'common.dropdownConfigRequired';
      return;
    }

    const safeDropdownObj = (this.dropdownObj ?? {}) as Partial<DropdownInfoConfig>;
    const hasDirectOptions = typeof safeDropdownObj === 'object'
      && safeDropdownObj !== null
      && Object.prototype.hasOwnProperty.call(safeDropdownObj, 'options')
      && Array.isArray(safeDropdownObj.options);

    if (hasDirectOptions) {
      this.sourceOptions = this.combineExtraOptions(this.resolvedConfig.options ?? []);
      this.options = this.mapOptions(this.sourceOptions);
      this.filteredOptions = [...this.options];
      this.optionsLoaded.emit({ name: this.resolvedConfig.name, options: this.sourceOptions });
      this.applyInitialValue();
      this.syncSearchTextFromValue();
      return;
    }

    if (this.resolvedConfig.options && this.resolvedConfig.options.length > 0) {
      this.sourceOptions = this.combineExtraOptions(this.resolvedConfig.options);
      this.options = this.mapOptions(this.sourceOptions);
      this.filteredOptions = [...this.options];
      this.optionsLoaded.emit({ name: this.resolvedConfig.name, options: this.sourceOptions });
      this.applyInitialValue();
      this.syncSearchTextFromValue();
      return;
    }

    if (!this.resolvedConfig.apiInfo) {
      this.applyInitialValue();
      return;
    }

    const apiInfo = this.resolveApiInfo(this.resolvedConfig.apiInfo);
    if (!apiInfo) {
      this.error = 'common.invalidDropdownApiInfo';
      return;
    }

    this.isLoading = true;
    const mergedFilters = {
      ...(apiInfo.filters ?? {}),
      ...(this.resolvedConfig.status === 0 || this.resolvedConfig.status === 1
        ? { status: this.resolvedConfig.status }
        : {}),
      ...(this.requestFilters ?? {})
    };

    this.api.postHandler(apiInfo, {
      pagination: false,
      filters: mergedFilters
    }).subscribe({
      next: (res: any) => {
        const loadedOptions = Array.isArray(res?.data) ? res.data : [];
        this.sourceOptions = this.combineExtraOptions(loadedOptions);
        this.options = this.mapOptions(this.sourceOptions);
        this.filteredOptions = [...this.options];
        this.optionsLoaded.emit({ name: this.resolvedConfig.name, options: this.sourceOptions });
        this.applyInitialValue();
        this.syncSearchTextFromValue();
        this.isLoading = false;
      },
      error: (err: any) => {
        this.error = err?.message || 'common.unableToLoadDropdownOptions';
        this.isLoading = false;
      }
    });
  }

  private applyInitialValue(): void {
    if (this.value !== null && this.value !== undefined && this.value !== '') {
      return;
    }

    if (this.resolvedConfig?.initialValue !== undefined) {
      this.value = this.resolvedConfig.initialValue;
      const matched = this.options.find((option) =>
        option.value === this.value
        || `${option.value ?? ''}` === `${this.value ?? ''}`
      );
      this.valueChange.emit({
        name: this.resolvedConfig.name,
        value: this.value,
        option: matched?.raw
      });
      return;
    }

    if (this.options.length === 1) {
      this.value = this.options[0].value;
      this.valueChange.emit({
        name: this.resolvedConfig.name,
        value: this.value,
        option: this.options[0].raw
      });
    }
  }

  private syncSearchTextFromValue(): void {
    const matched = this.options.find((option) =>
      option.value === this.value
      || `${option.value ?? ''}` === `${this.value ?? ''}`
    );
    this.searchText = matched?.display ?? '';
  }

  private mapOptions(source: DropdownOptionLike[]): DropdownViewOption[] {
    this.sourceOptions = source;
    const mapped = source.map((item) => {
      const value = this.readPathValue(item, this.resolvedConfig.valueKey);
      const display1 = this.readPathValue(item, this.resolvedConfig.display1);
      const display2 = this.resolvedConfig.display2
        ? this.readPathValue(item, this.resolvedConfig.display2)
        : undefined;
      const primary = this.translateDisplayValue(display1);
      const secondary = this.translateDisplayValue(display2);
      const display = secondary === undefined || secondary === null || secondary === ''
        ? `${primary ?? ''}`
        : `${primary ?? ''} [${secondary}]`;

      return {
        value,
        display,
        raw: item
      };
    });

    const seen = new Set<string>();
    const deduped = mapped.filter((item) => {
      const key = `${item.value ?? ''}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });

    if (!this.shouldAutoSortByDisplay()) {
      return deduped;
    }

    return [...deduped].sort((left, right) => this.compareOptions(left, right));
  }

  private shouldAutoSortByDisplay(): boolean {
    const apiInfo = this.resolvedConfig?.apiInfo;
    const endpointService = typeof apiInfo === 'string'
      ? apiInfo
      : apiInfo?.service;

    return endpointService === 'HOUSE_LIST'
      || endpointService === 'FLAT_LIST'
      || endpointService === 'RENTER_LIST'
      || endpointService === 'EXPENSE_HEAD_LIST';
  }

  private compareOptions(left: DropdownViewOption, right: DropdownViewOption): number {
    const leftPinnedOrder = this.getPinnedOrder(left);
    const rightPinnedOrder = this.getPinnedOrder(right);

    if (leftPinnedOrder !== rightPinnedOrder) {
      return leftPinnedOrder - rightPinnedOrder;
    }

    const leftPinnedToEnd = this.shouldPinExpenseHeadOptionToEnd(left);
    const rightPinnedToEnd = this.shouldPinExpenseHeadOptionToEnd(right);

    if (leftPinnedToEnd !== rightPinnedToEnd) {
      return leftPinnedToEnd ? 1 : -1;
    }

    return left.display.localeCompare(right.display, undefined, {
      sensitivity: 'base'
    });
  }

  private shouldPinExpenseHeadOptionToEnd(option: DropdownViewOption): boolean {
    const apiInfo = this.resolvedConfig?.apiInfo;
    const endpointService = typeof apiInfo === 'string'
      ? apiInfo
      : apiInfo?.service;

    if (endpointService !== 'EXPENSE_HEAD_LIST') {
      return false;
    }

    const expenseHeadName = `${this.readPathValue(option.raw, this.resolvedConfig.display1) ?? ''}`.trim();
    return expenseHeadName.localeCompare('Other', undefined, { sensitivity: 'base' }) === 0;
  }

  private getPinnedOrder(option: DropdownViewOption): number {
    if (option.raw['__pinToStart']) {
      return -1;
    }

    if (option.raw['__pinToEnd']) {
      return 1;
    }

    return 0;
  }

  private combineExtraOptions(source: DropdownOptionLike[]): DropdownOptionLike[] {
    const extraOptions = Array.isArray(this.resolvedConfig.extraOptions)
      ? this.resolvedConfig.extraOptions
      : [];

    if (extraOptions.length === 0) {
      return source;
    }

    return [...extraOptions, ...source];
  }

  private resolveApiInfo(apiInfo: ApiEndpointKey | ApiEndpointConfig): ApiEndpointConfig | null {
    if (typeof apiInfo !== 'string') {
      return apiInfo;
    }
    return ApiEndpoints[apiInfo] ?? null;
  }

  private readPathValue(source: DropdownOptionLike, path: string | undefined): unknown {
    if (!path) {
      return undefined;
    }
    if (!path.includes('.')) {
      return source[path];
    }

    return path.split('.').reduce<unknown>((acc, key) => {
      if (acc === null || acc === undefined || typeof acc !== 'object') {
        return undefined;
      }
      return (acc as Record<string, unknown>)[key];
    }, source);
  }

  private translateDisplayValue(value: unknown): string {
    const text = `${value ?? ''}`.trim();
    if (!text) {
      return '';
    }
    return this.language.translate(text, text);
  }
}
